import { DragIndicatorOutlined } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { memo } from 'react';

import {
    Supplier,
    SupplierActivationStatus,
} from 'entities/Property/model/types/PropertySchema';
import { SupplierLogo } from 'shared/ui/SupplierLogo';
import { VendorAccountStatusPill } from 'shared/ui/VendorAccountStatusPill/VendorAccountStatusPill';

export interface SupplierSourceRowProps {
    supplier: Supplier;
    isDraggable?: boolean;
    isNotUsed?: boolean;
    isDragging?: boolean;
    isDragDisabled?: boolean;
}
export const SupplierSourceRow = memo(
    ({
        supplier,
        isDraggable = false,
        isNotUsed = false,
        isDragging = false,
        isDragDisabled = false,
    }: SupplierSourceRowProps) => {
        const showStatus =
            supplier.operationsActivationStatus !==
            SupplierActivationStatus.Verified;

        const baseBackgroundColor = isNotUsed ? '#F8F8F8' : '#FFFFFF';
        const backgroundColor = isDragging ? '#FFFFFF' : baseBackgroundColor;

        const borderColor = isDragging ? '#DCDCDC' : 'transparent';

        return (
            <Box
                data-testclass="supplier-source-row"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px 18px 16px 16px',
                    borderRadius: '12px',
                    backgroundColor,
                    marginBottom: '12px',
                    flexGrow: 1,
                    cursor: !isDragDisabled ? 'ns-resize' : 'default',
                    transition: 'all 0.2s ease-in-out',
                    boxShadow: isDraggable
                        ? '0px 4px 17px 0px rgba(0, 0, 0, 0.10)'
                        : 'none',
                    maxWidth: '100%',
                    minWidth: '0',
                    position: 'relative',
                    border: `1px solid ${borderColor}`,
                    '&:hover': {
                        borderColor: isDragDisabled ? 'transparent' : '#DCDCDC',
                        backgroundColor: isDragDisabled
                            ? baseBackgroundColor
                            : '#FFFFFF',
                        '& .drag-indicator': {
                            opacity: 1,
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flex: 1,
                        gap: '10px',
                        width: '100%',
                    }}
                >
                    <SupplierLogo supplier={supplier} size={34} />
                    <Typography
                        data-testclass="supplier-name"
                        sx={{
                            flex: 1,
                            fontSize: '15px',
                            lineHeight: '24px',
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                            maxWidth: '100%',
                            color: isNotUsed
                                ? 'rgba(0, 0, 0, 0.50)'
                                : 'rgba(0, 0, 0, 0.85)',
                            transition: 'all 0.2s ease-in-out',
                        }}
                        title={supplier.name}
                    >
                        {supplier.name}
                    </Typography>

                    {showStatus && (
                        <VendorAccountStatusPill
                            vendor={supplier}
                            showDashOnEmptyStatus={false}
                        />
                    )}
                    {isDraggable && (
                        <DragIndicatorOutlined
                            className="drag-indicator"
                            sx={{
                                opacity: isDragging ? 1 : 0,
                                transition: 'all 0.2s ease-in-out',
                                position: 'absolute',
                                right: '0px',
                                color: '#B7B7B7',
                                fontSize: '18px',
                            }}
                        />
                    )}
                </Box>
            </Box>
        );
    },
);
